<script setup lang="ts">
// The suite's one country picker: the lib Select over the ISO 3166-1 list, named
// and sorted in the caller's language. Presentational only (ADR 0014) -- the lib
// has no i18n, so the host passes the language (`locale`) and the label of the
// empty entry (`noneLabel`).
import { computed } from 'vue';
import Select from './Select.vue';
import { countryOptions } from '../utils/countries';

const props = withDefaults(
  defineProps<{
    /** The UI language the names are written and sorted in (`de`, `en`, ...). */
    locale?: string;
    /** The empty entry's label. Country is optional, so choosing one stays undoable. */
    noneLabel?: string;
    invalid?: boolean;
    disabled?: boolean;
  }>(),
  { locale: 'en', noneLabel: '—', invalid: false, disabled: false },
);

// '' is "no country chosen": forms stage empty strings and drop them at save
// time, so the model stays a plain string. A null/undefined binding reads as ''.
const model = defineModel<string | null | undefined>({ default: '' });
const selected = computed({
  get: () => model.value || '',
  set: (value: string | number | undefined) => {
    model.value = String(value ?? '');
  },
});

// The empty entry is a real option rather than Select's `placeholder`, which
// renders `disabled hidden`.
const options = computed(() => [
  { value: '', label: props.noneLabel },
  ...countryOptions(props.locale).map((c) => ({ value: c.code, label: `${c.label} (${c.code})` })),
]);
</script>

<template>
  <Select v-model="selected" :options="options" :invalid="invalid" :disabled="disabled" />
</template>
